import React from 'react';

/**
 * SessionSummary - Visit session analytics and summary
 * Shows session stats, duration, participant info, etc.
 */
const SessionSummary = ({
  transcript,
  speakerSegments,
  soapNote,
  providerInfo,
  sessionType = 'unknown',
  connectionStatus = 'disconnected',
  isRecording = false,
  sessionDuration = 0,
  visitNoteStatus = 'pending'
}) => {
  // Calculate session statistics
  const getSessionStats = () => {
    const transcriptLength = transcript ? transcript.length : 0;
    const wordCount = transcript ? transcript.trim().split(/\s+/).length : 0;
    const speakerCount = speakerSegments ? new Set(speakerSegments.map(seg => seg.speaker)).size : 0;
    const noteLength = soapNote ? soapNote.length : 0;
    const noteWordCount = soapNote ? soapNote.trim().split(/\s+/).length : 0;

    return {
      transcriptLength,
      wordCount,
      speakerCount,
      noteLength,
      noteWordCount
    };
  };

  // Get speaker breakdown
  const getSpeakerBreakdown = () => {
    if (!speakerSegments || speakerSegments.length === 0) return [];

    const speakerStats = {};
    speakerSegments.forEach(segment => {
      const speaker = segment.speaker || 'Unknown';
      const wordCount = (segment.text || segment.content || '').trim().split(/\s+/).length;
      
      if (!speakerStats[speaker]) {
        speakerStats[speaker] = {
          name: speaker,
          segments: 0,
          words: 0
        };
      }
      
      speakerStats[speaker].segments += 1;
      speakerStats[speaker].words += wordCount;
    });

    return Object.values(speakerStats);
  };

  // Format duration (placeholder for future session timing)
  const formatDuration = (seconds) => {
    if (!seconds || seconds < 0) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Get session type info
  const getSessionTypeInfo = () => {
    switch (sessionType) {
      case 'zoom':
        return { icon: '📹', label: 'Zoom Meeting', color: '#2D8CFF' };
      case 'twilio':
        return { icon: '📞', label: 'Phone Call', color: '#F22F46' };
      case 'in-person':
        return { icon: '🎤', label: 'In-Person', color: '#00D924' };
      default:
        return { icon: '🔊', label: 'Audio Session', color: '#666' };
    }
  };

  // Get connection status info
  const getConnectionStatusInfo = () => {
    switch (connectionStatus) {
      case 'connected':
        return { icon: '🟢', label: 'Connected', color: '#4caf50' };
      case 'connecting':
        return { icon: '🟡', label: 'Connecting', color: '#ff9800' };
      case 'disconnected':
        return { icon: '🔴', label: 'Disconnected', color: '#f44336' };
      case 'error':
        return { icon: '❌', label: 'Error', color: '#f44336' };
      default:
        return { icon: '⚪', label: 'Unknown', color: '#666' };
    }
  };

  const stats = getSessionStats();
  const speakerBreakdown = getSpeakerBreakdown();
  const sessionTypeInfo = getSessionTypeInfo();
  const connectionInfo = getConnectionStatusInfo();

  return (
    <div className="card mb-3 border-0 overflow-auto">
      <div className="templates-header">
        <div className="templates-header-main">
          <h3 className="popup-title mb-0">Session Summary</h3>
          <div className="template-header-actions text-muted">
            {new Date().toLocaleDateString()}
          </div>
        </div>
      </div>

      <div className="card-body">
        {/* Session Overview */}
        <div className="mb-4">
          <h4 className="h6 mb-3">Visit Overview</h4>
          <div className="row text-center">
            <div className="col-6 col-md-3 mb-3">
              <div className="display-6 fw-bold">{stats.wordCount}</div>
              <div className="text-muted">Words Spoken</div>
            </div>
            <div className="col-6 col-md-3 mb-3">
              <div className="display-6 fw-bold">{stats.speakerCount}</div>
              <div className="text-muted">Participants</div>
            </div>
            <div className="col-6 col-md-3 mb-3">
              <div className="display-6 fw-bold">{stats.noteWordCount}</div>
              <div className="text-muted">Note Words</div>
            </div>
            <div className="col-6 col-md-3 mb-3">
              <div className="display-6 fw-bold">{formatDuration(sessionDuration)}</div>
              <div className="text-muted">Duration</div>
            </div>
          </div>
        </div>

        {/* Audio Session Status */}
        <div className="mb-4">
          <h4 className="h6 mb-3">Audio Session Status</h4>
          <div className="row g-3">
            <div className="col-md-6">
              <div className="card h-100">
                <div className="card-body d-flex flex-column justify-content-between">
                  <div className="d-flex align-items-center mb-2">
                    <span className="me-2" style={{ color: sessionTypeInfo.color }}>
                      {sessionTypeInfo.icon}
                    </span>
                    <span className="fw-bold">{sessionTypeInfo.label}</span>
                  </div>
                  <div className="text-muted" style={{ color: connectionInfo.color }}>
                    {connectionInfo.icon} {connectionInfo.label}
                  </div>
                </div>
              </div>
            </div>
            
            <div className="col-md-6">
              <div className="card h-100">
                <div className="card-body d-flex flex-column justify-content-between">
                  <div className="mb-2">
                    {isRecording ? (
                      <span className="badge bg-success">
                        <span className="spinner-grow spinner-grow-sm me-1" role="status" aria-hidden="true"></span>
                        🎙️ Recording
                      </span>
                    ) : (
                      <span className="badge bg-secondary">⏹️ Not Recording</span>
                    )}
                  </div>
                  <div className="text-muted">
                    {visitNoteStatus === 'generating' && '⏳ Generating Note'}
                    {visitNoteStatus === 'completed' && '✅ Note Ready'}
                    {visitNoteStatus === 'error' && '❌ Note Error'}
                    {visitNoteStatus === 'pending' && '⏸️ Note Pending'}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Provider Information */}
        {providerInfo && (
          <div className="mb-4">
            <h4 className="h6 mb-3">Provider</h4>
            <div className="card">
              <div className="card-body d-flex align-items-center">
                <div className="avatar-circle p-3 bg-primary text-white me-3">
                  {providerInfo.name ? providerInfo.name.charAt(0).toUpperCase() : 'P'}
                </div>
                <div>
                  <div className="fw-bold">{providerInfo.name || 'Provider'}</div>
                  {providerInfo.healthSystem && (
                    <div className="text-muted">{providerInfo.healthSystem}</div>
                  )}
                  {providerInfo.id && (
                    <div className="text-muted">ID: {providerInfo.id}</div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Speaker Breakdown */}
        {speakerBreakdown.length > 0 && (
          <div className="mb-4">
            <h4 className="h6 mb-3">Conversation Breakdown</h4>
            <ul className="list-group">
              {speakerBreakdown.map((speaker, index) => (
                <li key={index} className="list-group-item d-flex justify-content-between align-items-center">
                  <div>
                    <div className="fw-bold">{speaker.name}</div>
                    <div className="text-muted small">
                      {speaker.segments} segments • {speaker.words} words
                    </div>
                  </div>
                  <span className="badge bg-info rounded-pill">
                    {stats.wordCount > 0
                      ? Math.round((speaker.words / stats.wordCount) * 100)
                      : 0}%
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Documentation Status */}
        <div className="mb-4">
          <h4 className="h6 mb-3">Documentation Status</h4>
          <div className="row g-3">
            <div className="col-md-6">
              <div className="card h-100">
                <div className="card-body d-flex align-items-center">
                  <div className={`me-3 fs-4 ${transcript ? 'text-success' : 'text-muted'}`}>
                    {transcript ? '✓' : '○'}
                  </div>
                  <div>
                    <div className="fw-bold">Live Transcription</div>
                    <div className="text-muted">
                      {transcript ? 'Active' : 'Not started'}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="col-md-6">
              <div className="card h-100">
                <div className="card-body d-flex align-items-center">
                  <div className={`me-3 fs-4 ${soapNote ? 'text-success' : 'text-muted'}`}>
                    {soapNote ? '✓' : '○'}
                  </div>
                  <div>
                    <div className="fw-bold">Visit Notes</div>
                    <div className="text-muted">
                      {soapNote ? 'Generated' : 'Pending'}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Empty State */}
        {!transcript && !soapNote && speakerBreakdown.length === 0 && (
          <div className="empty-state">
            <div className="empty-icon">📊</div>
            <p>Session summary will appear here</p>
            <small>Start your visit to see analytics and statistics</small>
          </div>
        )}
      </div>

    </div>
  );
};

export default SessionSummary;